import React, { useState, useEffect } from "react";
import { Tag, Calendar, Search, Clock, Award, Ticket, Gift, ChevronRight, Copy, CheckCircle, AlertCircle, ArrowRight, X } from "lucide-react";

const OffersComponent = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [copiedCode, setCopiedCode] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await fetch("https://apigrand.dodunsoftsolutions.com/api/voucher/all");
        if (!response.ok) {
          throw new Error("Failed to fetch offers");
        }
        const data = await response.json();
        setOffers(data.vouchers);
      } catch (err) {
        console.error("Error fetching offers:", err);
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  // Copy voucher code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
      })
      .catch(err => console.error('Failed to copy: ', err));
  };

  // Filter offers by category and search query
  const getFilteredOffers = () => {
    return offers.filter((offer) => {
      const matchesSearch = searchQuery === "" ||
        offer.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (offer.description && offer.description.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory =
        activeCategory === "all" ||
        (activeCategory === "percentage" && offer.discountType === "percentage") ||
        (activeCategory === "fixed" && offer.discountType === "fixed") ||
        (activeCategory === "expiring" && new Date(offer.expiryDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));

      return matchesSearch && matchesCategory;
    });
  };

  const filteredOffers = getFilteredOffers();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const isExpiringSoon = (dateString) => {
    if (!dateString) return false;
    const expiryDate = new Date(dateString);
    const weekFromNow = new Date();
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    return expiryDate < weekFromNow && expiryDate > new Date();
  };

  const getRandomGradient = (id) => {
    const gradients = [
      "from-red-600 to-red-500",
      "from-red-600 to-amber-500",
      "from-amber-600 to-amber-500",
      "from-red-700 to-red-600"
    ];

    // Use id to get a consistent gradient for each offer
    const numberFromId = id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return gradients[numberFromId % gradients.length];
  };

  // Calculate days remaining until expiry
  const getDaysRemaining = (dateString) => {
    if (!dateString) return 0;
    const today = new Date();
    const expiryDate = new Date(dateString);
    const timeLeft = expiryDate - today;
    return Math.max(0, Math.ceil(timeLeft / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pb-16">
      {/* Header Section with decorative elements */}
      <div className="relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-red-50 to-transparent"></div>
        <div className="absolute top-10 left-10 w-16 h-16 rounded-full bg-red-100 opacity-50"></div>
        <div className="absolute top-20 right-20 w-24 h-24 rounded-full bg-amber-100 opacity-50"></div>

        <div className="max-w-6xl mx-auto px-4 relative mt-10 z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center mb-5">
              <span className="relative inline-block">
                <span className="absolute inset-0 transform translate-x-1.5 translate-y-1.5 bg-red-200 rounded-lg"></span>
                <span className="relative block bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-3 rounded-lg shadow-lg">
                  <Gift size={24} />
                </span>
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">
              Exclusive <span className="text-red-600">Offers</span> & Vouchers
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Unlock premium savings with our exclusive collection of vouchers and special offers.
            </p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-xl shadow-lg p-4 mb-10 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="relative w-full md:w-auto md:flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by code or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2 w-full md:w-auto overflow-x-auto pb-1 md:pb-0 no-scrollbar">
              <button
                onClick={() => setActiveCategory("all")}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${activeCategory === "all"
                    ? "bg-red-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                All Vouchers
              </button>
              <button
                onClick={() => setActiveCategory("percentage")}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${activeCategory === "percentage"
                    ? "bg-red-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Percentage Off
              </button>
              <button
                onClick={() => setActiveCategory("fixed")}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${activeCategory === "fixed"
                    ? "bg-red-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Fixed Amount
              </button>
              <button
                onClick={() => setActiveCategory("expiring")}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${activeCategory === "expiring"
                    ? "bg-red-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Ending Soon
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Offer Listings */}
      <div className="max-w-6xl mx-auto px-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 animate-pulse font-medium">Loading premium offers...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16 bg-red-50 text-red-700 rounded-lg border border-red-200 shadow-sm">
            <AlertCircle size={48} className="mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Unable to Load Offers</h3>
            <p className="mb-4 text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg shadow-sm transition-colors font-medium inline-flex items-center"
            >
              <ArrowRight size={18} className="mr-2" /> Try Again
            </button>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
              <Tag size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No Matching Offers
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              We couldn't find any vouchers matching your search criteria.
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
                setActiveCategory("all");
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg shadow-sm transition-colors font-medium"
            >
              View All Offers
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredOffers.map((offer) => (
              <div
                key={offer._id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300 overflow-hidden flex flex-col group"
              >
                <div className="h-48 bg-gray-100 flex items-center justify-center">
                  {offer.image ? (
                    <img
                      src={offer.image}
                      alt={offer.code}
                      className="w-full h-full object-cover rounded-t-md"
                    />
                  ) : (
                    <div className="text-gray-400">No Image</div>
                  )}
                </div>

                {/* Offer Body */}
                <div className="p-5 flex-grow">
                  <div className="flex justify-between items-center mb-3">
                    <div className="bg-red-50 text-red-700 font-bold text-sm px-3 py-1 rounded-md border border-red-100">
                      {offer.code}
                    </div>
                    <div className="text-xs text-gray-500 font-medium">
                      Min spend: ₹{offer.minAmount}
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1 group-hover:text-red-600 transition-colors">
                    {offer.title || `${offer.discountType === "percentage" ? `${offer.discountValue}% Off` : `₹${offer.discountValue} Off`} Voucher`}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[40px]">
                    {offer.description || "Use this exclusive voucher code to get a special discount on your next booking."}
                  </p>

                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <Calendar className="h-4 w-4 mr-2 text-red-500" />
                    <span>Valid till {formatDate(offer.expiryDate)}</span>
                  </div>

                  <div className="flex items-center text-sm text-gray-500">
                    <Ticket className="h-4 w-4 mr-2 text-red-500" />
                    <span>Applicable on movie tickets and concessions</span>
                  </div>
                </div>

                {/* Offer Footer */}
                <div className="p-5 pt-0 mt-3 flex items-center justify-between">
                  <button
                    onClick={() => setSelectedOffer(offer)}
                    className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center"
                  >
                    View Details
                    <ChevronRight size={16} className="ml-1" />
                  </button>

                  <button
                    onClick={() => copyToClipboard(offer.code)}
                    className={`flex items-center font-medium py-2 px-4 rounded-lg transition-colors shadow-sm
                    ${copiedCode === offer.code
                        ? 'bg-green-600 text-white'
                        : 'bg-red-600 hover:bg-red-700 text-white'} transition-colors shadow-sm`}
                  >
                    {copiedCode === offer.code ? (
                      <>
                        <CheckCircle size={16} className="mr-1.5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={16} className="mr-1.5" />
                        Copy Code
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Offer Details Modal */}
      {selectedOffer && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-scaleIn shadow-2xl">
            <div
              className="h-48 relative p-6 bg-cover bg-center rounded-t-2xl"
              style={{ backgroundImage: `url(${selectedOffer.image})` }}
            >
              <button
                onClick={() => setSelectedOffer(null)}
                className="absolute top-4 right-4 p-1 bg-white/20 hover:bg-white/40 rounded-full text-white"
              >
                <X size={20} />
              </button>
              <div className="text-center mt-2">
                <div className="text-white text-sm font-medium">VOUCHER DETAILS</div>
                <div className="text-white text-3xl font-bold mt-1">
                  {selectedOffer.discountType === "percentage"
                    ? `${selectedOffer.discountValue}% OFF`
                    : `₹${selectedOffer.discountValue} OFF`}
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="bg-red-50 text-red-700 font-bold text-lg px-4 py-1.5 rounded-md border border-red-100">
                  {selectedOffer.code}
                </div>
                <button
                  onClick={() => copyToClipboard(selectedOffer.code)}
                  className={`flex items-center font-medium py-2 
                  ${copiedCode === selectedOffer.code
                      ? 'text-green-600'
                      : 'text-red-600 hover:text-red-700'} transition-colors shadow-sm`}
                >
                  {copiedCode === selectedOffer.code ? (
                    <>
                      <CheckCircle size={16} className="mr-1.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={16} className="mr-1.5" />
                      Copy
                    </>
                  )}
                </button>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {selectedOffer.title || `${selectedOffer.discountType === "percentage" ? `${selectedOffer.discountValue}% Off` : `₹${selectedOffer.discountValue} Off`} Voucher`}
              </h3>

              <div className="bg-gray-50 p-4 rounded-xl mb-5 space-y-3">
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 mr-3 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-800">Valid Till</p>
                    <p className="text-sm text-gray-600">{formatDate(selectedOffer.expiryDate)}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Tag className="h-5 w-5 mr-3 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-800">Minimum Order</p>
                    <p className="text-sm text-gray-600">₹{selectedOffer.minAmount || "0"}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Ticket className="h-5 w-5 mr-3 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-800">Applicable On</p>
                    <p className="text-sm text-gray-600">Movie tickets and concessions</p>
                  </div>
                </div>
              </div>

              <div className="mb-5">
                <h4 className="font-medium text-gray-800 mb-2">Description</h4>
                <p className="text-gray-600 text-sm">
                  {selectedOffer.description || "Use this exclusive voucher code to get a special discount on your next booking. Apply at checkout to redeem your savings."}
                </p>
              </div>

              <div className="mb-5">
                <h4 className="font-medium text-gray-800 mb-2">Terms & Conditions</h4>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Cannot be combined with other offers</li>
                  <li>• Valid for one-time use only</li>
                  <li>• Minimum order value must be met before discount is applied</li>
                  <li>• Valid till {formatDate(selectedOffer.expiryDate)}</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setSelectedOffer(null)}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    copyToClipboard(selectedOffer.code);
                    setSelectedOffer(null);
                  }}
                  className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center"
                >
                  <Copy size={16} className="mr-1.5" />
                  Copy & Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for animations and utilities */}
      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar {
            display: none;
        }
        .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default OffersComponent;